import type {
  ClientLoaderFunction,
  ClientLoaderFunctionArgs,
} from "react-router";
import { parseURL } from "ufo";

import { SiteSdk } from "~/core/site-sdk";
import type { loader } from "~/root";
import type { ParsedAuthCookieData } from "~/routes/api.auth.me";
import { normalizeUrl } from "~/shared/utils/normalize-url";
import { parseConditionTemplate } from "~/shared/utils/parse-condition-template";
import { evaluateCmsExpression } from "~/shared/utils/parse-string-expression-to-boolean";
import { ROOM_URL_TEMPLATES } from "~/shared/utils/parse-url-template-and-build-href";

const getSafeAuthData = async (
  siteSdk: SiteSdk,
): Promise<ParsedAuthCookieData> => {
  try {
    const authData = await siteSdk.getUserAuthData();
    if (typeof authData !== "object" || authData === null) {
      return {};
    }
    return authData;
  } catch {
    return {};
  }
};

export const rootClientLoader = (async ({
  serverLoader,
  request,
}: ClientLoaderFunctionArgs) => {
  const serverData: Awaited<ReturnType<typeof loader>> = await serverLoader();
  const siteSdk = new SiteSdk();
  const authData = await getSafeAuthData(siteSdk);
  if (!serverData.redirectForClient) {
    return { ...serverData, ...authData };
  }
  const redirectForClientEntity =
    serverData?.redirectForClient?.[0]?.redirect_entity_id;
  if (!redirectForClientEntity) return { ...serverData, ...authData };

  let parsedCondition: boolean | string | number = false;
  try {
    parsedCondition = evaluateCmsExpression(
      parseConditionTemplate(redirectForClientEntity.condition ?? "", {
        AUTH_TOKEN: String(!!authData.authorizationToken),
        IS_NEW_USER: String(false),
      }),
    );
  } catch {
    parsedCondition = false;
  }

  if (parsedCondition) {
    let preparedHrefTo: string | undefined;

    try {
      if (redirectForClientEntity?.to?.includes(ROOM_URL_TEMPLATES.ROOM_URL)) {
        const template = redirectForClientEntity?.to;
        const locale = serverData?.language;

        if (template && locale) {
          const { redirectUrl } = await siteSdk.getParsedRoomUrlFromTemplate({
            locale,
            template,
          });
          preparedHrefTo = redirectUrl;
        }
      } else {
        const query = redirectForClientEntity?.preserve_query
          ? parseURL(request.url).search
          : "";
        preparedHrefTo =
          normalizeUrl(redirectForClientEntity?.to ?? "") + query;
      }
    } catch {
      preparedHrefTo = undefined;
    }

    if (preparedHrefTo) {
      window.location.replace(preparedHrefTo);
    }
  }
  return { ...serverData, ...authData };
}) satisfies ClientLoaderFunction;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
rootClientLoader.hydrate = true as const;
